import { useState } from "react";
import styled from "styled-components";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { StatusBadge } from "../../components/StatusBadge";
import { IndividualCheckResponse } from "../../types/Individual";
import {
  checkStatusIntent,
  Panel,
  PanelHeader,
  StatusIcon,
  SubHeading,
} from "./IdLivenessPrimitives";
import {
  ImageViewer,
  RotatableImage,
  ThumbButton,
  useImageRotations,
  ViewableImage,
} from "./ImageViewer";

type Instance = IndividualCheckResponse["instance"];

/**
 * Liveness takes a third and face match two thirds, since face match holds up
 * to three images and the review reason. Either one alone takes the full
 * width, and the two stack when the panel is narrow.
 */
const Split = styled.div`
  ${({ theme: { space } }) => `
    display: flex;
    flex-wrap: wrap;
    gap: ${space[4]};

    & > * {
      flex: 1 1 240px;
      min-width: 0;
    }
    & > * + * {
      flex: 2 1 480px;
    }
  `}
`;

const ReasonBox = styled.div`
  ${({ theme: { colors, radii, space } }) => `
    margin-top: ${space[2]};
    padding: ${space[2]};
    background: ${colors.gray[5]};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[0]};
  `}
`;

const SectionHeader = ({
  title,
  status,
}: {
  title: string;
  status?: string;
}) => (
  <Flex alignItems="center" justifyContent="space-between" gap="8" mb={2}>
    <Flex alignItems="center" gap="8">
      {status && <StatusIcon status={status} size="18px" />}
      <Paragraph m="0px" fontWeight="600" color="gray.80">
        {title}
      </Paragraph>
    </Flex>
    {status && (
      <StatusBadge intent={checkStatusIntent(status)}>{status}</StatusBadge>
    )}
  </Flex>
);

/** Each image gets an equal share of its half, within these bounds. */
const ImageTile = styled(Box)`
  flex: 1 1 180px;
  min-width: 0;
  max-width: 280px;
`;

const Thumb = ({
  image,
  rotation,
  onOpen,
}: {
  image: ViewableImage;
  rotation: number;
  onOpen: () => void;
}) =>
  image.base64 ? (
    <ThumbButton
      type="button"
      aria-label={`View ${image.label} full size`}
      onClick={onOpen}
    >
      <RotatableImage
        base64={image.base64}
        alt={image.label}
        rotation={rotation}
        maxHeight={280}
      />
    </ThumbButton>
  ) : null;

/**
 * Liveness and face match side by side in one card (stacked when narrow),
 * each keeping its own status, since they are separate checks. Any image
 * opens full size.
 */
export const BiometricsPanel = ({
  liveness,
  faceMatch,
  livenessImages,
  faceMatchImages,
}: {
  liveness?: Instance["liveness"];
  faceMatch?: Instance["face_match"];
  livenessImages?: string[];
  faceMatchImages?: string[];
}) => {
  const { rotationOf, rotate } = useImageRotations();
  const [viewing, setViewing] = useState<ViewableImage | null>(null);

  if (!liveness && !faceMatch) return null;

  const livenessItems: (ViewableImage & { status?: string | null })[] = (
    liveness?.images ?? []
  ).map((image, i) => ({
    key: `liveness-${i}`,
    label: image?.name || "Liveness image",
    downloadName: `liveness-${i + 1}.jpg`,
    base64: livenessImages?.[i],
    status: image?.status,
  }));

  // The face match images are fetched only for entries with a path, so the
  // fetched list lines up with that filtered list, not the full one.
  const faceItems: ViewableImage[] = (faceMatch?.images ?? [])
    .filter((image) => image?.path)
    .map((image, i) => ({
      key: `face-${i}`,
      label: image?.name || "Face match image",
      downloadName: `face-match-${i + 1}.jpg`,
      base64: faceMatchImages?.[i],
    }));

  const title =
    liveness && faceMatch
      ? "Liveness and face match"
      : liveness
      ? liveness.title || "Liveness"
      : faceMatch.title || "Face match";

  return (
    <Panel>
      <PanelHeader title={title} />
      <Split>
        {liveness && (
          <Box minWidth={0}>
            <SectionHeader
              title={liveness.title || "Liveness"}
              status={liveness.status}
            />
            <Flex gap="16" flexWrap="wrap">
              {livenessItems.map((item) => (
                <ImageTile key={item.key}>
                  <Thumb
                    image={item}
                    rotation={rotationOf(item.key)}
                    onOpen={() => setViewing(item)}
                  />
                  <Paragraph
                    m="0px"
                    mt={1}
                    fontSize={1}
                    fontWeight="600"
                    color="gray.80"
                  >
                    {item.label}
                  </Paragraph>
                  {item.status && (
                    <StatusBadge intent={checkStatusIntent(item.status)} mt={1}>
                      {item.status}
                    </StatusBadge>
                  )}
                </ImageTile>
              ))}
            </Flex>
          </Box>
        )}

        {faceMatch && (
          <Box minWidth={0}>
            <SectionHeader
              title={faceMatch.title || "Face match"}
              status={faceMatch.status}
            />
            <Flex gap="16" flexWrap="wrap">
              {faceItems.map((item) => (
                <ImageTile key={item.key}>
                  <Thumb
                    image={item}
                    rotation={rotationOf(item.key)}
                    onOpen={() => setViewing(item)}
                  />
                  <Paragraph
                    m="0px"
                    mt={1}
                    fontSize={1}
                    fontWeight="600"
                    color="gray.80"
                  >
                    {item.label}
                  </Paragraph>
                </ImageTile>
              ))}
            </Flex>
            <ReasonBox>
              <SubHeading>Review reason</SubHeading>
              {faceMatch.results?.length > 0 ? (
                faceMatch.results.map((reason, i) => (
                  <Paragraph key={i} m="0px" fontSize={1} color="gray.80">
                    {reason}
                  </Paragraph>
                ))
              ) : (
                <Paragraph m="0px" fontSize={1} color="gray.60">
                  None
                </Paragraph>
              )}
            </ReasonBox>
          </Box>
        )}
      </Split>

      <ImageViewer
        image={viewing}
        rotation={viewing ? rotationOf(viewing.key) : 0}
        onRotate={() => viewing && rotate(viewing.key)}
        onClose={() => setViewing(null)}
      />
    </Panel>
  );
};
